import SearchInput from "@dashboard/components/AppLayout/ListFilters/components/SearchInput";
import { TopNav } from "@dashboard/components/AppLayout/TopNav";
import { Callout } from "@dashboard/components/Callout/Callout";
import { DashboardCard } from "@dashboard/components/Card";
import { Date as DateCell } from "@dashboard/components/Date/Date";
import { ListPageLayout } from "@dashboard/components/Layouts/List/Root";
import { ResponsiveTable } from "@dashboard/components/ResponsiveTable/ResponsiveTable";
import { TablePagination } from "@dashboard/components/TablePagination/TablePagination";
import TableRowLink from "@dashboard/components/TableRowLink/TableRowLink";
import { type AtticAssetDirectoryFragment, type AtticAssetFragment } from "@dashboard/graphql";
import { useNotifier } from "@dashboard/hooks/useNotifier/useNotifier";
import { sectionNames } from "@dashboard/intl";
import { renderCollection } from "@dashboard/misc";
import { TableBody, TableCell, TableFooter, TableHead } from "@material-ui/core";
import { Box, Button, Skeleton, Text, TrashBinIcon, vars } from "@saleor/macaw-ui-next";
import type React from "react";
import { useEffect, useState } from "react";
import { useIntl } from "react-intl";

import { directoryListMessages, fileListMessages, sectionMessages } from "../../messages";
import { AtticAssetsDestinationIcon } from "../../topNav";
import { atticAssetDirectoryListPath } from "../../urls";
import { formatBytes, getThumbnailSrc, THUMBNAIL_RETRY_DELAYS_MS } from "../../utils";
import { AtticAssetDirectoryBadges } from "../AtticAssetDirectoryBadges/AtticAssetDirectoryBadges";

const numberOfColumns = 7;
const THUMBNAIL_SIZE = 48;

export interface AtticAssetDirectoryPageProps {
  directory: AtticAssetDirectoryFragment;
  /** undefined while loading. */
  assets: AtticAssetFragment[] | undefined;
  assetsLoadFailed: boolean;
  query: string;
  rowNumber: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
  /**
   * The upload card with its own queue state (AtticAssetUploader), shown when
   * the directory is writable. A node rather than the queue's props, so an
   * upload's progress never re-renders this page.
   */
  uploader: React.ReactNode;
  onQueryChange: (query: string) => void;
  onRowNumberChange: (rowNumber: number) => void;
  onNextPage: () => void;
  onPreviousPage: () => void;
  onDelete: (asset: AtticAssetFragment) => void;
}

const ThumbnailPlaceholder = ({ filename }: { filename: string }) => (
  <Box
    __width={`${THUMBNAIL_SIZE}px`}
    __height={`${THUMBNAIL_SIZE}px`}
    borderRadius={2}
    backgroundColor="default2"
    display="flex"
    alignItems="center"
    justifyContent="center"
    data-test-id="attic-asset-thumbnail-placeholder"
  >
    <Text size={1} color="default2" fontWeight="medium">
      {(filename.split(".").pop() ?? "").toUpperCase().slice(0, 4)}
    </Text>
  </Box>
);

interface ThumbnailLoad {
  src: string | null;
  /** Tries that failed so far. */
  failures: number;
  /** A failed try is waiting for its retry. */
  waiting: boolean;
}

const Thumbnail = ({ asset }: { asset: AtticAssetFragment }) => {
  const src = getThumbnailSrc(asset);
  const [load, setLoad] = useState<ThumbnailLoad>({ src, failures: 0, waiting: false });
  // A new URL (a refetch) starts over.
  const current = load.src === src ? load : { src, failures: 0, waiting: false };
  const gaveUp = current.failures > THUMBNAIL_RETRY_DELAYS_MS.length;

  useEffect(() => {
    if (!current.waiting || gaveUp) {
      return;
    }

    const timer = window.setTimeout(
      () => setLoad({ src, failures: current.failures, waiting: false }),
      THUMBNAIL_RETRY_DELAYS_MS[current.failures - 1],
    );

    return () => window.clearTimeout(timer);
  }, [src, current.waiting, current.failures, gaveUp]);

  if (!src || current.waiting || gaveUp) {
    return <ThumbnailPlaceholder filename={asset.filename} />;
  }

  return (
    <img
      // A fresh element per try, so the browser asks again.
      key={current.failures}
      src={src}
      alt=""
      loading="lazy"
      width={THUMBNAIL_SIZE}
      height={THUMBNAIL_SIZE}
      onError={() => setLoad({ src, failures: current.failures + 1, waiting: true })}
      style={{
        objectFit: "contain",
        borderRadius: 4,
        background: vars.colors.background.default2,
      }}
      data-test-id="attic-asset-thumbnail"
    />
  );
};

const COPIED_FOR_MS = 2000;

/**
 * Not the shared useClipboard: that one ignores a refused or missing
 * clipboard (insecure origin, denied permission), and the merchant would
 * paste whatever was there before. A failure here says so, with the URL.
 */
const CopyUrlButton = ({ url, filename }: { url: string; filename: string }) => {
  const intl = useIntl();
  const notify = useNotifier();
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!copied) {
      return;
    }

    const timer = window.setTimeout(() => setCopied(false), COPIED_FOR_MS);

    return () => window.clearTimeout(timer);
  }, [copied]);

  const copy = async () => {
    try {
      if (!navigator.clipboard?.writeText) {
        throw new Error("clipboard unavailable");
      }

      await navigator.clipboard.writeText(url);
      setCopied(true);
    } catch (error) {
      notify({
        status: "error",
        text: intl.formatMessage(fileListMessages.copyFailed, { url }),
      });
    }
  };

  return (
    <Button
      variant="secondary"
      size="small"
      onClick={copy}
      aria-label={intl.formatMessage(fileListMessages.copyUrlLabel, { filename })}
      data-test-id="attic-asset-copy-url"
    >
      {intl.formatMessage(copied ? fileListMessages.copied : fileListMessages.copyUrl)}
    </Button>
  );
};

export const AtticAssetDirectoryPage = ({
  directory,
  assets,
  assetsLoadFailed,
  query,
  rowNumber,
  hasNextPage,
  hasPreviousPage,
  uploader,
  onQueryChange,
  onRowNumberChange,
  onNextPage,
  onPreviousPage,
  onDelete,
}: AtticAssetDirectoryPageProps) => {
  const intl = useIntl();

  return (
    <ListPageLayout>
      <TopNav
        href={atticAssetDirectoryListPath}
        hrefIcon={<AtticAssetsDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.allDirectories)}
        title={directory.name}
        subtitle={
          <Box display="flex" alignItems="center" gap={3} flexWrap="wrap">
            <Text size={2} color="default2">
              /{directory.slug}
            </Text>
            <Text size={2} color="default2">
              {intl.formatMessage(fileListMessages.fileCount, {
                count: directory.objectCount,
                size: formatBytes(directory.byteCount, intl.locale),
              })}
            </Text>
            <AtticAssetDirectoryBadges directory={directory} />
          </Box>
        }
      />
      <Box display="flex" flexDirection="column" gap={4} paddingBottom={6}>
        {!directory.writable && (
          <Box paddingX={6} data-test-id="attic-asset-read-only">
            <Callout type="info" title={intl.formatMessage(fileListMessages.readOnlyTitle)}>
              {intl.formatMessage(fileListMessages.readOnlyBody, {
                manager: directory.claimedBy || intl.formatMessage(fileListMessages.anApp),
              })}
            </Callout>
          </Box>
        )}
        {uploader}
        <DashboardCard data-test-id="attic-asset-list">
          <DashboardCard.Content paddingX={0}>
            <Box paddingX={6} paddingTop={4} paddingBottom={2}>
              <SearchInput
                initialSearch={query}
                placeholder={intl.formatMessage(fileListMessages.searchPlaceholder, {
                  slug: directory.slug,
                })}
                onSearchChange={onQueryChange}
              />
            </Box>
            <ResponsiveTable>
              <TableHead>
                <TableRowLink>
                  <TableCell>{intl.formatMessage(fileListMessages.preview)}</TableCell>
                  <TableCell>{intl.formatMessage(fileListMessages.file)}</TableCell>
                  <TableCell>{intl.formatMessage(fileListMessages.type)}</TableCell>
                  <TableCell align="right">{intl.formatMessage(fileListMessages.size)}</TableCell>
                  <TableCell>{intl.formatMessage(fileListMessages.dimensions)}</TableCell>
                  <TableCell>{intl.formatMessage(fileListMessages.created)}</TableCell>
                  <TableCell />
                </TableRowLink>
              </TableHead>
              <TableBody>
                {assetsLoadFailed ? (
                  <TableRowLink data-test-id="attic-asset-list-failed">
                    <TableCell colSpan={numberOfColumns}>
                      <Text size={3} color="critical1">
                        {intl.formatMessage(fileListMessages.loadFailed)}
                      </Text>
                    </TableCell>
                  </TableRowLink>
                ) : (
                  renderCollection(
                    assets,
                    row => (
                      // No href: a file has no page of its own to open.
                      <TableRowLink key={row ? row.id : "skeleton"} data-test-id="attic-asset-row">
                        <TableCell>{row ? <Thumbnail asset={row} /> : <Skeleton />}</TableCell>
                        <TableCell>
                          {row ? (
                            <>
                              <Text size={3} display="block" __wordBreak="break-all">
                                {row.filename}
                              </Text>
                              <Text
                                size={2}
                                color="default2"
                                display="block"
                                __wordBreak="break-all"
                              >
                                {row.path}
                              </Text>
                            </>
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell>
                          {row ? (
                            <Text size={2} color="default2">
                              {row.contentType}
                            </Text>
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell align="right">
                          {row ? formatBytes(row.size, intl.locale) : <Skeleton />}
                        </TableCell>
                        <TableCell>
                          {row ? (
                            row.width && row.height ? (
                              `${row.width} × ${row.height}`
                            ) : (
                              "-"
                            )
                          ) : (
                            <Skeleton />
                          )}
                        </TableCell>
                        <TableCell>
                          {row ? <DateCell date={row.createdAt} /> : <Skeleton />}
                        </TableCell>
                        <TableCell align="right">
                          {row && (
                            <Box
                              display="flex"
                              gap={1}
                              justifyContent="flex-end"
                              alignItems="center"
                            >
                              {row.url && <CopyUrlButton url={row.url} filename={row.path} />}
                              {directory.writable && (
                                <Button
                                  variant="tertiary"
                                  icon={<TrashBinIcon />}
                                  title={intl.formatMessage(fileListMessages.delete)}
                                  aria-label={intl.formatMessage(fileListMessages.deleteLabel, {
                                    filename: row.path,
                                  })}
                                  onClick={() => onDelete(row)}
                                  data-test-id="attic-asset-delete"
                                />
                              )}
                            </Box>
                          )}
                        </TableCell>
                      </TableRowLink>
                    ),
                    () => (
                      <TableRowLink data-test-id="attic-asset-list-empty">
                        <TableCell colSpan={numberOfColumns}>
                          <Text size={3} color="default2">
                            {intl.formatMessage(
                              query ? fileListMessages.noMatches : fileListMessages.noFiles,
                            )}
                          </Text>
                        </TableCell>
                      </TableRowLink>
                    ),
                  )
                )}
              </TableBody>
              <TableFooter>
                <TableRowLink>
                  <TablePagination
                    colSpan={numberOfColumns}
                    settings={{ rowNumber }}
                    onUpdateListSettings={(_, value) => onRowNumberChange(value as number)}
                    hasNextPage={hasNextPage}
                    hasPreviousPage={hasPreviousPage}
                    onNextPage={onNextPage}
                    onPreviousPage={onPreviousPage}
                  />
                </TableRowLink>
              </TableFooter>
            </ResponsiveTable>
          </DashboardCard.Content>
        </DashboardCard>
      </Box>
    </ListPageLayout>
  );
};

export default AtticAssetDirectoryPage;

/**
 * The directory page's frame when there is no directory to show: gone
 * (deleted, or a stale link), or the read failed.
 */
export const AtticAssetDirectoryMissing = ({ loadFailed = false }: { loadFailed?: boolean }) => {
  const intl = useIntl();

  return (
    <ListPageLayout>
      <TopNav
        href={atticAssetDirectoryListPath}
        hrefIcon={<AtticAssetsDestinationIcon />}
        hrefTitle={intl.formatMessage(sectionMessages.allDirectories)}
        title={intl.formatMessage(sectionNames.atticDigitalAssets)}
      />
      <DashboardCard>
        <DashboardCard.Content>
          {loadFailed ? (
            <Text size={3} color="critical1" data-test-id="attic-asset-directory-load-failed">
              {intl.formatMessage(directoryListMessages.loadFailed)}
            </Text>
          ) : (
            <Text size={3} color="default2" data-test-id="attic-asset-directory-missing">
              {intl.formatMessage(sectionMessages.notFound)}
            </Text>
          )}
        </DashboardCard.Content>
      </DashboardCard>
    </ListPageLayout>
  );
};
